import { Observable, Subject, combineLatest, concat, filter, last, map, take, takeUntil } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { OCC_CART_ID_CURRENT, OCC_USER_ID_ANONYMOUS, OccEndpointsService, Product, UserIdService, WindowRef } from '@spartacus/core';
import { ActiveCartService, MultiCartService } from '@spartacus/cart/base/core';
import { Cart, MultiCartFacade, OrderEntry } from '@spartacus/cart/base/root';
import { BaseStoreService } from './base-store.service';
import { CartGroupTypeEnum } from '../enums';
import { CartGroupEntry } from '../models';

@Injectable({
  providedIn: 'root'
})
export class CustomActiveCartService extends ActiveCartService implements OnDestroy {
  protected readonly destroy$: Subject<void> = new Subject();
  constructor(
    protected multiCartFacade: MultiCartFacade,
    protected userIdService: UserIdService,
    protected winRef: WindowRef,
    protected multiCartService: MultiCartService,
    private occEndpointsService: OccEndpointsService,
    private baseStoreService: BaseStoreService,
    private http: HttpClient
  ) {
    super(multiCartService, userIdService, winRef);
  }

  protected loadOrMerge(cartId: string, userId: string, previousUserId: string): void {
    // for login user, whenever there's an existing cart, we will load the user
    // current cart and merge it into the existing cart
    // cartId will be defined (not '', null, undefined)
    if (cartId === OCC_CART_ID_CURRENT) {
      this.multiCartService.loadCart({
        userId,
        cartId: OCC_CART_ID_CURRENT,
        extraData: {
          active: true
        }
      });
    } else if (
      userId !== previousUserId &&
      userId !== OCC_USER_ID_ANONYMOUS &&
      previousUserId !== OCC_USER_ID_ANONYMOUS
    ) {
      // This case covers the case when you are logged in and then asm user logs in and you don't want to merge, but only load emulated user cart
      // Similarly when you are logged in as asm user and you logout and want to resume previous user session
      this.multiCartService.loadCart({
        userId,
        cartId,
        extraData: {
          active: true
        }
      });
    } else {
      // We have particular cart locally, but we logged in, so we need to combine this with current cart or make it ours.
      const multiCart$ = this.multiCartService.getCartEntity(cartId).pipe(
        filter((cartEntity) => cartEntity.success && !cartEntity.loading),
        take(1)
      );
      const isGuestCart$ = this.isGuestCart();
      combineLatest([multiCart$, isGuestCart$]).subscribe(([cartEntity, isGuest]) => {
        if (cartEntity.value?.entryGroups?.length > 0) {
          if (isGuest) {
            // If cart is guest, we need to create a new cart with current entries.
            this.guestCartMerge(cartId);
          } else {
            // If anonymous cart is not empty, we only keep anonymous cart.
            this.multiCartService.createCart({
              userId,
              oldCartId: cartId,
              extraData: {
                active: true
              }
            });
          }
        } else {
          // If anonymous cart is empty, we load current user cart.
          this.multiCartService.deleteCart(cartId, OCC_USER_ID_ANONYMOUS);
          this.multiCartService.loadCart({
            userId,
            cartId: OCC_CART_ID_CURRENT,
            extraData: {
              active: true
            }
          });
        }
      });
    }
  }

  protected guestCartMerge(cartId: string): void {
    this.multiCartService.deleteCart(cartId, OCC_USER_ID_ANONYMOUS);
    this.multiCartService.createCart({
      userId: OCC_CART_ID_CURRENT,
      extraData: {
        active: true
      }
    });
    const guestCart$ = this.multiCartService.getCartEntity(cartId).pipe(
      map((cartEntity) => cartEntity.value),
      take(1)
    );
    const newCart$ = this.requireLoadedCart(true);
    combineLatest([guestCart$, newCart$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([guestCart, newCartState]: [Cart, Cart]) => {
        const actions = [];
        if (guestCart.isForfait) {
          actions.push(this.addGuestForfaitToNewCart(guestCart.entryGroups[0].entries[0].product, newCartState.code));
        } else {
          guestCart.entryGroups.forEach((entryGroup) => {
            if (entryGroup.type === CartGroupTypeEnum.STANDALONE) {
              entryGroup.entries.forEach((entry) =>
                actions.push(this.addGuestStandaloneEntryToNewCart(entry, newCartState.code))
              );
            } else if (entryGroup.type === CartGroupTypeEnum.BUNDLE) {
              actions.push(this.addGuestBundleToNewCart(entryGroup, newCartState.code));
            }
          });
        }
        concat(...actions)
          .pipe(last())
          .subscribe(() => {
            this.multiCartService.reloadCart(newCartState.code);
          });
      });
  }

  private addGuestStandaloneEntryToNewCart(entry: OrderEntry, cartId: string): Observable<any> {
    const url = this.occEndpointsService.buildUrl('addEntries', {
      urlParams: { userId: OCC_CART_ID_CURRENT, cartId },
      queryParams: { code: entry.product.code, qty: entry.quantity }
    });
    return this.http.post(url, {});
  }

  private addGuestBundleToNewCart(groupEntry: CartGroupEntry, cartId: string): Observable<any> {
    const url = this.occEndpointsService.buildUrl('addBundle', {
      urlParams: { userId: OCC_CART_ID_CURRENT, cartId },
      queryParams: { bundleID: groupEntry.externalReferenceId, qty: groupEntry.selectedQuantity }
    });
    return this.http.post(url, {});
  }

  private addGuestForfaitToNewCart(forfait: Product, cartId: string): Observable<any> {
    const baseStore = this.baseStoreService.baseStore;
    const url = this.occEndpointsService.buildUrl('addEntries', {
      urlParams: { userId: OCC_CART_ID_CURRENT, cartId },
      queryParams: { code: forfait.code, pointOfService: baseStore.defaultDeliveryOrigin?.name }
    });
    const body = {
      product: { code: forfait.code },
      quantity: 1,
      deliveryPointOfService: { name: baseStore.defaultDeliveryOrigin?.name }
    };
    return this.http.post(url, body);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
